/**
 * Typed, validated access to build-time configuration.
 * Reading `import.meta.env` anywhere else is a lint-visible smell: this module
 * is the single place environment shape is asserted.
 */
interface AppEnv {
  readonly apiBaseUrl: string;
  /**
   * Development sign-in bypass: `email|group-guid`, sent as `X-Debug-User`.
   * Used only when Entra is not configured, and never in a production build —
   * see `debugUser` below.
   */
  readonly debugUser: string;
  /**
   * Microsoft Entra ID. The SPA runs the authorization-code + PKCE flow itself
   * and exchanges the resulting identity token at `/auth/login`, so the backend
   * issues the session — Entra only proves who the user is.
   *
   * Nothing secret belongs here: a SPA cannot hold a client secret, and this
   * flow does not need one.
   */
  readonly auth: {
    readonly clientId: string;
    readonly tenantId: string;
    readonly authority: string;
    /** Must be registered on the app registration verbatim. */
    readonly redirectUri: string;
    /** True once the tenant details are present and sign-in can be attempted. */
    readonly isConfigured: boolean;
  };
  readonly enableMocks: boolean;
  readonly mockRole: string;
  readonly isProduction: boolean;
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function readBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'string') return value === 'true';
  return fallback;
}

const raw = import.meta.env;

const clientId = readString(raw.VITE_AZURE_CLIENT_ID);
const tenantId = readString(raw.VITE_AZURE_TENANT_ID);
const authConfigured = clientId !== '' && tenantId !== '';

export const env: AppEnv = {
  apiBaseUrl: readString(raw.VITE_API_BASE_URL, '/api/v1'),
  // Refused in production builds regardless of what the environment supplies:
  // a debug identity header that survived into a deployed bundle would be an
  // authentication bypass, not a misconfiguration. It is also dropped as soon
  // as Entra is configured, so the two identities can never both be in play.
  debugUser: raw.PROD || authConfigured ? '' : readString(raw.VITE_DEBUG_USER),
  auth: {
    clientId,
    tenantId,
    authority: tenantId ? `https://login.microsoftonline.com/${tenantId}` : '',
    redirectUri: readString(raw.VITE_AZURE_REDIRECT_URI, globalThis.location.origin),
    isConfigured: authConfigured,
  },
  enableMocks: readBoolean(raw.VITE_ENABLE_MSW, !raw.PROD),
  mockRole: readString(raw.VITE_MOCK_ROLE, 'platform_admin'),
  isProduction: raw.PROD,
};
